import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CatalogNativeSelect from "./CatalogNativeSelect";
import {
  CATALOG_DB_COLUMN_LABELS,
  PRODUCT_COLUMN_LABELS,
  formatHardDeleteRuleLabel,
} from "./utils/catalogFieldLabels";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";
import { CELL_MATCH_MODE_OPTIONS, normalizeCellMatchMode } from "./utils/cellMatchModeUtils";

export const HARD_DELETE_MAX_RULES = 3;
export const HARD_DELETE_TONE_CLASS = "cm-hard-del-tone";

const HardDeletePanel = ({
  productColumns = [],
  catalogColumns = [],
  rules,
  onRulesChange,
  onExecuteAll,
  executing = false,
  disabled = false,
  className = "",
  showMatchesOnly = false,
  onShowMatchesOnlyChange,
}) => {
  const [selectedColumn, setSelectedColumn] = useState("");
  const [ruleValue, setRuleValue] = useState("");
  const [matchMode, setMatchMode] = useState("eq");

  const columnOptions = useMemo(() => [
    ...productColumns.map((column) => ({
      value: `product:${column}`,
      label: `products.${PRODUCT_COLUMN_LABELS[column] || column}`,
    })),
    ...catalogColumns.map((column) => ({
      value: `catalog:${column}`,
      label: `catalogs.${CATALOG_DB_COLUMN_LABELS[column] || column}`,
    })),
  ], [productColumns, catalogColumns]);

  const handleAddRule = () => {
    if (disabled || !selectedColumn) {
      return;
    }
    if (rules.length >= HARD_DELETE_MAX_RULES) {
      return;
    }

    const [table, column] = String(selectedColumn).split(":");
    if (!table || !column) {
      return;
    }

    const normalizedValue = String(ruleValue ?? "").trim();
    const normalizedMode = normalizeCellMatchMode(matchMode);

    const exists = rules.some(
      (rule) => rule.table === table
        && rule.column === column
        && rule.value === normalizedValue
        && (rule.mode || "eq") === normalizedMode,
    );
    if (exists) {
      return;
    }

    onRulesChange([
      ...rules,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        table,
        column,
        value: normalizedValue,
        mode: normalizedMode,
      },
    ]);
    setRuleValue("");
  };

  const handleRemoveRule = (ruleId) => {
    if (disabled || executing) {
      return;
    }
    onRulesChange(rules.filter((rule) => rule.id !== ruleId));
  };

  return (
    <div className={`cm-hard-delete-panel${className ? ` ${className}` : ""}`.trim()}>
      <div className="cm-hard-delete-form">
        <span className="cm-hard-delete-heading" aria-hidden="true">
          <span>HARD</span>
          <span>DEL</span>
        </span>
        <CatalogNativeSelect
          className="cm-hard-delete-column-select"
          value={selectedColumn}
          onChange={setSelectedColumn}
          options={columnOptions}
          placeholder="Colonna"
          ariaLabel="Colonna hard delete"
          disabled={disabled || executing}
        />
        <CatalogNativeSelect
          className="cm-cell-match-mode-select"
          value={matchMode}
          onChange={setMatchMode}
          options={CELL_MATCH_MODE_OPTIONS}
          placeholder="Mode"
          ariaLabel="Modalita confronto hard delete"
          disabled={disabled || executing}
        />
        <input
          type="text"
          className="cm-input cm-hard-delete-value-field"
          placeholder="Valore cella (vuoto = null/empty)"
          value={ruleValue}
          disabled={disabled || executing}
          onChange={(event) => setRuleValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleAddRule();
            }
          }}
        />
        <button
          type="button"
          className="cm-btn cm-btn-secondary cm-hard-delete-add-btn"
          onClick={handleAddRule}
          disabled={disabled || executing || !selectedColumn || rules.length >= HARD_DELETE_MAX_RULES}
          title={CM_TOOLTIPS.aggiungiAbbinamentoHardDelete}
        >
          Aggiungi abbinamento
        </button>
        <button
          type="button"
          className="cm-btn cm-btn-danger cm-hard-delete-run-all-btn"
          onClick={() => onExecuteAll?.(rules)}
          disabled={disabled || executing || rules.length === 0}
          title={CM_TOOLTIPS.eseguiHardDelete}
        >
          {executing ? "Eliminazione..." : "Esegui"}
        </button>
        <button
          type="button"
          className={`cm-btn cm-btn-secondary cm-tool-matches-btn${showMatchesOnly ? " is-active" : ""}`}
          onClick={() => onShowMatchesOnlyChange?.(!showMatchesOnly)}
          disabled={disabled || executing || rules.length === 0 || !onShowMatchesOnlyChange}
          title="Mostra solo righe catalogo che verranno eliminate (AND di tutti gli abbinamenti)"
        >
          Solo Match
        </button>
      </div>

      {rules.length > 0 && (
        <div className="cm-hard-delete-rules">
          {rules.map((rule) => (
            <div key={rule.id} className={`cm-hard-delete-card ${HARD_DELETE_TONE_CLASS}`}>
              <span className="cm-hard-delete-card-label">
                {formatHardDeleteRuleLabel(rule)}
              </span>
              <div className="cm-hard-delete-card-actions">
                <IconButton
                  size="small"
                  aria-label="Rimuovi abbinamento hard delete"
                  title={CM_TOOLTIPS.rimuoviAbbinamentoHardDelete}
                  disabled={disabled || executing}
                  onClick={() => handleRemoveRule(rule.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

HardDeletePanel.propTypes = {
  productColumns: PropTypes.arrayOf(PropTypes.string),
  catalogColumns: PropTypes.arrayOf(PropTypes.string),
  rules: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    table: PropTypes.string.isRequired,
    column: PropTypes.string.isRequired,
    value: PropTypes.string,
    mode: PropTypes.string,
  })).isRequired,
  onRulesChange: PropTypes.func.isRequired,
  onExecuteAll: PropTypes.func.isRequired,
  executing: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  showMatchesOnly: PropTypes.bool,
  onShowMatchesOnlyChange: PropTypes.func,
};

export default HardDeletePanel;
